/**
 * The one schema a repository environment is written in. Backend routes,
 * setup runs and the settings page all read the same shape: the type is
 * inferred from the schema, so a field added here reaches every reader without
 * a second hand-written interface to keep in step.
 *
 * `QaEnvironmentConfig` is the parsed (output) type: every field with a
 * default is present. Request bodies are `QaEnvironmentConfigInput`, where the
 * defaults are optional. A stored row written before a field existed still
 * parses, because every field added later carries a default.
 *
 * This module runs in the browser too, so it uses nothing from Node.
 */
import { z } from "zod";
import { toolchainPlanSchema } from "./toolchainPlanTypes.js";
import { COGNITO_REGION, DEFAULT_QA_RECORDING, MAX_QA_RECORDING_BYTES, MAX_QA_RECORDING_SECONDS, MAX_QA_SCREENSHOT_BYTES, MAX_QA_SCREENSHOT_COUNT, QA_RUN_DEADLINE_MS, cognitoIdpOrigin, } from "./qa.js";
/** Ports the runner itself listens on inside the sandbox. */
export const QA_CONTROL_PORTS = new Set([5900, 6080, 6081, 6090, 6092, 9222, 9333, 49983]);
/** A literal IP host as the URL parser serialises one: dotted IPv4 or bracketed IPv6. */
function isIpHostname(hostname) {
    return hostname.startsWith("[") || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}
/**
 * Names a sandbox must never be allowed to reach even though they look
 * public: private suffixes, wildcard-DNS services that resolve any embedded
 * IP (a public name for 169.254.169.254), Fly's private network, and our own
 * hosts.
 */
const RESERVED_HOST_SUFFIXES = [
    "localhost",
    "local",
    "internal",
    "flycast",
    "nip.io",
    "sslip.io",
    "xip.io",
    "yagni.app",
];
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
function isPublicHostname(raw) {
    // A trailing dot is the same name to DNS and would slip past a suffix check.
    const hostname = raw.toLowerCase().replace(/\.$/, "");
    if (isIpHostname(hostname))
        return false;
    const labels = hostname.split(".");
    if (labels.length < 2 || !labels.every((label) => HOST_LABEL.test(label)))
        return false;
    if (/^\d+$/.test(labels[labels.length - 1]))
        return false;
    if (labels[0] === "metadata")
        return false;
    return !RESERVED_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));
}
/** One shell command a setup, service or check runs. */
export const qaCommandSchema = z.string().trim().min(1).max(1000);
const command = qaCommandSchema;
export const qaServiceNameSchema = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/);
const serviceName = qaServiceNameSchema;
/**
 * A tool the environment declares, as mise accepts it: the tool's name, `@`,
 * then a version or range (`kubectl@1.31`, `aqua:cli/cli@2`, `node@lts`).
 */
export const qaDeclaredToolSchema = z
    .string()
    .max(200)
    .regex(/^[A-Za-z0-9_.:/-]+@\S(?:[^\x00-\x1f]*\S)?$/, "Name a tool and its version as name@version");
/** A repository a build reads, whole (`owner/name`) or from a path (`owner/name/docs/`). */
export const qaReadSchema = z
    .string()
    .max(300)
    .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+(?:\/[^\x00-\x1f\\]*)?$/, "Name a repository as owner/name, optionally followed by a path")
    .refine((value) => !value.split("/").some((part) => part === ".."), "Name a path inside the repository");
export const qaPathSchema = z
    .string()
    .max(500)
    .refine((value) => value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\") &&
    !/[\r\n]/.test(value), "Use an application-relative path");
export const qaPortSchema = z
    .number()
    .int()
    .min(1024)
    .max(65535)
    .refine((value) => !QA_CONTROL_PORTS.has(value), "This port is reserved for the runner");
const port = qaPortSchema;
const path = qaPathSchema;
/** A managed service beside the app (Postgres, Redis, MySQL): a port, or off. */
const managedService = z.object({ port }).strict().nullable().default(null);
export const publicOrigin = z
    .string()
    .max(300)
    .refine((value) => {
    try {
        const url = new URL(value);
        return (url.protocol === "https:" &&
            !url.username &&
            !url.password &&
            url.pathname === "/" &&
            !url.search &&
            !url.hash &&
            isPublicHostname(url.hostname));
    }
    catch {
        return false;
    }
}, "Use a public HTTPS origin without credentials");
export const variableName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/);
export const qaVariables = z
    .record(variableName, z.string().max(4096))
    .refine((value) => Object.keys(value).length <= 50, "Use at most 50 variables");
const repoId = z.uuid().transform((value) => value.toLowerCase());
const PREVIEW_RESERVED_HEADERS = new Set([
    "cookie",
    "set-cookie",
    "host",
    "origin",
    "referer",
    "connection",
    "content-length",
    "transfer-encoding",
    "te",
    "trailer",
    "upgrade",
    "keep-alive",
    "accept-encoding",
    "forwarded",
    "x-real-ip",
    "x-http-method-override",
]);
const secretName = z
    .string()
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/)
    .max(100);
/** Origin-scoped headers that open a protected preview deployment. */
export const qaPreviewAccessSchema = z
    .array(z
    .object({
    origin: z
        .string()
        .max(300)
        .refine((value) => {
        try {
            const url = new URL(value);
            return (url.protocol === "https:" &&
                url.origin === value &&
                !url.username &&
                !url.password &&
                !url.hostname.includes("*"));
        }
        catch {
            return false;
        }
    }, "Use an exact HTTPS preview origin"),
    headers: z
        .array(z
        .object({
        name: z
            .string()
            .min(1)
            .max(80)
            .regex(/^[A-Za-z][A-Za-z0-9-]*$/)
            .transform((value) => value.toLowerCase())
            .refine((value) => !PREVIEW_RESERVED_HEADERS.has(value) &&
            !/^(sec-|proxy-|x-forwarded-|access-control-)/.test(value), "Choose an access header, not a browser or transport header"),
        secretName,
    })
        .strict())
        .min(1)
        .max(5)
        .refine((headers) => new Set(headers.map((header) => header.name)).size === headers.length, "Each access header must be unique"),
})
    .strict())
    .max(5)
    .refine((entries) => new Set(entries.map((entry) => entry.origin)).size === entries.length, "Select each preview origin once");
/**
 * An AWS Cognito user pool signed in from the backend, never from the sandbox.
 * Every value that is a secret is named here and stored as an environment
 * credential; the pool, client and origin are plain configuration.
 */
export const qaCognitoAuthSchema = z
    .object({
    region: z.string().max(30).regex(COGNITO_REGION, "Name the pool's AWS region"),
    userPoolId: z
        .string()
        .max(100)
        .regex(/^[a-z]{2}(-[a-z]+)+-\d_[A-Za-z0-9]{1,64}$/, "Name the Cognito user pool id"),
    clientId: z.string().regex(/^[a-z0-9]{1,128}$/, "Name the Cognito app client id"),
    usernameSecret: secretName,
    passwordSecret: secretName,
    /** The app client's secret, when the client is a confidential one. */
    clientSecretName: secretName.optional(),
    /** Base32 TOTP seed of the test identity's authenticator app. */
    totpSecretName: secretName.optional(),
    /** The application origin whose local storage receives the tokens. */
    storageOrigin: z
        .string()
        .max(300)
        .refine((value) => {
        try {
            const url = new URL(value);
            if (url.origin !== value || url.username || url.password)
                return false;
            return (url.protocol === "https:" ||
                (url.protocol === "http:" && ["127.0.0.1", "localhost"].includes(url.hostname)));
        }
        catch {
            return false;
        }
    }, "Name the application origin that receives the session"),
})
    .strict()
    .refine((value) => value.userPoolId.startsWith(`${value.region}_`), "The user pool id must belong to the named region")
    .refine((value) => new Set([
    value.usernameSecret,
    value.passwordSecret,
    ...(value.clientSecretName ? [value.clientSecretName] : []),
    ...(value.totpSecretName ? [value.totpSecretName] : []),
]).size ===
    2 + (value.clientSecretName ? 1 : 0) + (value.totpSecretName ? 1 : 0), "Select a different saved credential for each Cognito value");
/**
 * An origin a run may install browser state into: an approved application
 * origin, or one of this environment's own sandbox services. Sign-in provider
 * origins are never installable; state belongs to the application under test.
 *
 * `providers` names the sign-in hosts this field additionally refuses. The
 * human-sign-in field keeps refusing Google and the pool's API endpoint: the
 * endpoint is required in `allowedOrigins` for Cognito sign-in to work, and
 * that approval must not double as permission to install state there. It is
 * an API host, so nothing legitimate could already name it, unlike a hosted
 * sign-in domain, which is why `amazoncognito.com` is refused only for the
 * session origin and would invalidate already-saved environments here.
 */
const COGNITO_IDP_HOST = /^cognito-idp\.[a-z0-9-]+\.amazonaws\.com$/;
export function installableOrigin(value, origin, providers = [/(^|\.)google\.com$/, COGNITO_IDP_HOST]) {
    try {
        const url = new URL(origin);
        // A trailing dot is the same host to DNS but not to a suffix test.
        const hostname = url.hostname.replace(/\.$/, "");
        if (url.origin !== origin || providers.some((host) => host.test(hostname)))
            return false;
        return (value.allowedOrigins.includes(origin) ||
            value.services.some((service) => origin === `http://127.0.0.1:${service.port}` ||
                origin === `http://localhost:${service.port}`));
    }
    catch {
        return false;
    }
}
const SIGN_IN_PROVIDERS = [
    /(^|\.)google\.com$/,
    COGNITO_IDP_HOST,
    // Refused for the session origin only: a hosted sign-in domain is a
    // plausible entry in an already-saved human-sign-in environment.
    /(^|\.)amazoncognito\.com$/,
];
/**
 * Preview credentials go only to approved application origins, never to a
 * sign-in provider's host.
 */
export function approvedPreviewAccess(config) {
    return (config.previewAccess ?? []).every((entry) => {
        try {
            return (config.allowedOrigins.includes(entry.origin) &&
                !(config.authOrigins ?? []).some((origin) => new URL(origin).hostname === new URL(entry.origin).hostname) &&
                !/(^|\.)google(?:usercontent)?\.com$/.test(new URL(entry.origin).hostname));
        }
        catch {
            return false;
        }
    });
}
/**
 * How the test identity signs in. `command` is the unattended sign-in command;
 * `recipe` and `bootstrap` are the two older names for it, read as `command`
 * so every stored row keeps working until they are retired.
 */
const AUTH_MODES = ["public", "command", "recipe", "bootstrap", "human", "cognito"];
const authMode = z.preprocess((value) => (value === "recipe" || value === "bootstrap" ? "command" : value), z.enum(AUTH_MODES).optional());
/**
 * The runtime each language runs on, chosen per repository by preflight. A
 * value that names a baked line (`"22"`, `"3.12"`, `"3.3"`, `"1.23"`, `"8.3"`) runs on
 * the baked toolchain; any other value is a version the run installs before
 * its setup commands (`"16.7.0"`, `"3.11"`, `"3.3.0"`, `"1.25.1"`).
 */
export const qaRuntimesSchema = z
    .object({
    node: z
        .string()
        .regex(/^\d{1,2}(?:\.\d{1,3}){0,2}$/)
        .optional(),
    python: z
        .string()
        .regex(/^3\.\d{1,2}(?:\.\d{1,3})?$/)
        .optional(),
    ruby: z
        .string()
        .regex(/^\d\.\d{1,2}(?:\.\d{1,3})?$/)
        .optional(),
    go: z
        .string()
        .regex(/^1\.\d{1,3}(?:\.\d{1,3})?$/)
        .optional(),
    // The baked line (`"8"`), a channel whose newest SDK the run installs
    // (`"9.0"`), or an exact SDK release (`"8.0.100"`).
    dotnet: z
        .string()
        .regex(/^\d{1,2}(?:\.\d{1,2}(?:\.\d{3})?)?$/)
        .optional(),
    // A baked PHP line (`"7.4"`, `"8.3"`): PHP is never installed by a run.
    php: z
        .string()
        .regex(/^\d\.\d{1,2}$/)
        .optional(),
})
    .strict();
/** A compose service's name as Compose accepts it. */
export const composeServiceName = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,62}$/);
/** A compose file inside a checkout: relative, inside it, a YAML file. */
export const composeFilePathSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((value) => !value.startsWith("/") &&
    !value.split("/").includes("..") &&
    !/[\r\n\\\x00]/.test(value) &&
    /\.ya?ml$/i.test(value), "Name a compose file inside the repository");
const composeFilePath = composeFilePathSchema;
/**
 * Where this repository's environment is defined when another repository
 * holds it: a compose file in a sibling checkout, the services it starts, and
 * what it says about this repository's own service.
 */
export const environmentFromSchema = z
    .object({
    repoId,
    composeFile: composeFilePath,
    /** The compose services started, never this repository's own. */
    services: z
        .array(composeServiceName)
        .max(20)
        .default([])
        .refine((names) => new Set(names).size === names.length, "Name each service once"),
    /** This repository's service in the compose file, when it has one. Never started. */
    appService: composeServiceName.nullable().default(null),
    /** The app service's own variables, rewritten for an app on the sandbox's loopback. */
    env: qaVariables.default({}),
    /** Variables the compose file expects the host to supply, by name only. */
    needs: z.array(variableName).max(50).default([]),
    /** Loopback ports published for started services that publish none themselves. */
    ports: z
        .array(z.object({ service: composeServiceName, port }).strict())
        .max(20)
        .default([]),
    source: z.enum(["detected", "person"]).default("person"),
})
    .strict()
    .refine((value) => !value.appService || !value.services.includes(value.appService), "This repository's own service is never started from the compose file")
    .refine((value) => value.ports.every((entry) => value.services.includes(entry.service)), "Publish ports only for services the compose file starts");
export const qaEnvironmentSchema = z
    .object({
    version: z.literal(2).default(2),
    previewAccess: qaPreviewAccessSchema.optional(),
    environmentDefaults: qaVariables.optional(),
    generatedVariables: z
        .array(z.object({ name: variableName, encoding: z.enum(["hex", "base64"]) }).strict())
        .max(50)
        .refine((values) => new Set(values.map((value) => value.name)).size === values.length)
        .optional(),
    /**
     * Additional repositories checked out beside the primary repo. `pin` decides
     * how `sha` is refreshed: "fixed" keeps the sha resolved when the revision
     * was saved; "branch" re-resolves `ref` when a run is requested.
     */
    dependencies: z
        .array(z
        .object({
        repoId,
        ref: z
            .string()
            .trim()
            .min(1)
            .max(250)
            .regex(/^[^\s\x00-\x1f]+$/),
        sha: z.string().regex(/^[0-9a-f]{40}$/),
        pin: z.enum(["fixed", "branch"]).default("fixed"),
        setupCommands: z.array(command).max(20).default([]),
        /** Variables applied only to this repository's setup commands and services. */
        environmentDefaults: qaVariables.optional(),
    })
        .strict())
        .max(20, "Check out at most 20 dependency repositories")
        .optional(),
    /**
     * The environment is defined in a sibling repository's compose file (spec
     * 2026-10-27): that repository is one of `dependencies`, and `services`
     * start from `composeFile` in its checkout before this repository's setup
     * commands. Absent: never decided, so setup may detect one. Null: a person
     * cleared it, and detection leaves it cleared.
     */
    environmentFrom: environmentFromSchema.nullable().optional(),
    postgres: managedService,
    redis: managedService,
    mysql: managedService,
    /** Which runtime versions the run puts on `PATH`, baked or installed. Written by preflight. */
    runtimes: qaRuntimesSchema.default({}),
    /**
     * Every tool the repository declares, resolved under the toolchain policy
     * (baked or installed, and what could not be). Written by preflight beside
     * `runtimes`, which stays the source for the runtime lines for one release.
     */
    toolchain: toolchainPlanSchema.optional(),
    /** False makes the run build-only: install, start and test, no browser. */
    browser: z.boolean().default(true),
    target: z.enum(["sandbox", "deployment"]).default("sandbox"),
    setupCommands: z.array(command).max(20).default([]),
    /**
     * Tools the environment declares beyond what the repository's files name,
     * as `name@version`; preflight adds each as a pinned request.
     */
    declaredTools: z
        .array(qaDeclaredToolSchema)
        .max(40)
        .default([])
        .refine((values) => new Set(values).size === values.length, "Declare each tool once"),
    /** Run once in the home directory after the toolchain installs, before dependencies. */
    toolInstallCommands: z.array(command).max(20).default([]),
    /** Workspace variables the environment needs, by name only; a missing one fails setup. */
    requiredVariables: z
        .array(variableName)
        .max(50)
        .default([])
        .refine((values) => new Set(values).size === values.length, "Name each variable once"),
    /** Repositories (or paths in them) a build reads as documentation. */
    reads: z
        .array(qaReadSchema)
        .max(20)
        .default([])
        .refine((values) => new Set(values).size === values.length, "Name each read once"),
    fixtures: z
        .array(z
        .object({
        name: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/),
        description: z.string().min(1).max(300),
        command,
    })
        .strict())
        .max(10)
        .default([])
        .refine((items) => new Set(items.map((item) => item.name)).size === items.length, "Test-state names must be unique"),
    commandTimeoutMs: z.number().int().min(1).max(QA_RUN_DEADLINE_MS).optional(),
    verifyCommand: command.nullable().default(null),
    browserTestCommand: command.nullable().default(null),
    services: z
        .array(z
        .object({
        repoId: repoId.optional(),
        name: serviceName,
        command,
        cwd: z
            .string()
            .max(200)
            .refine((value) => !value.startsWith("/") &&
            !value.split("/").includes("..") &&
            !/[\r\n\\]/.test(value))
            .default("."),
        port,
        readinessPath: path.default("/"),
        readinessTimeoutMs: z.number().int().min(1).max(QA_RUN_DEADLINE_MS).optional(),
        /** Variables applied only to this service, over the repository and environment defaults. */
        env: qaVariables.optional(),
    })
        .strict())
        .max(8)
        .default([]),
    browserService: serviceName.optional(),
    firstCheck: z
        .object({
        selector: z.string().trim().min(1).max(300),
        text: z.string().trim().min(1).max(500).optional(),
    })
        .strict()
        .optional(),
    entryPath: path.default("/"),
    authMode,
    cognitoAuth: qaCognitoAuthSchema.optional(),
    authOrigins: z.array(publicOrigin).max(20).optional(),
    authStateOrigins: z.array(z.string().max(300)).max(20).optional(),
    authCommand: command.nullable().default(null),
    authCheckSelector: z.string().min(1).max(300).nullable().default(null),
    allowedOrigins: z.array(publicOrigin).max(40).default([]),
    installationOrigins: z.array(publicOrigin).max(40).default(["https://registry.npmjs.org"]),
    screenshots: z
        .object({
        maxCount: z.number().int().min(1).max(MAX_QA_SCREENSHOT_COUNT),
        maxBytes: z
            .number()
            .int()
            .min(1024 * 1024)
            .max(MAX_QA_SCREENSHOT_BYTES),
    })
        .strict()
        .optional(),
    recording: z
        .object({
        maxSeconds: z.number().int().min(60).max(MAX_QA_RECORDING_SECONDS),
        maxBytes: z
            .number()
            .int()
            .min(8 * 1024 * 1024)
            .max(MAX_QA_RECORDING_BYTES),
    })
        .strict()
        .default(DEFAULT_QA_RECORDING),
    viewport: z
        .object({
        width: z.number().int().min(320).max(1920),
        height: z.number().int().min(320).max(1080),
    })
        .default({ width: 1280, height: 800 }),
    maskSelectors: z.array(z.string().min(1).max(300)).max(30).default([]),
    /**
     * Retired (YAG-1066): nobody confirms test data, and nothing reads this.
     * Still accepted so stored configurations and older clients parse; a save
     * stores it true for the releases before.
     */
    testDataConfirmed: z.boolean().default(false),
})
    .strict()
    .refine(approvedPreviewAccess, "Preview credentials require approved application origins, excluding sign-in providers")
    .refine((value) => !value.browserService ||
    value.services.some((service) => service.name === value.browserService), "Choose an existing application service for the browser")
    .refine(
// With dependency checkouts, services[0] is no longer an obvious choice:
// it can belong to a dependency. Never fall back to it silently.
(value) => !value.dependencies?.length ||
    value.target !== "sandbox" ||
    !value.services.length ||
    Boolean(value.browserService), "Choose the browser service explicitly when the environment includes dependency repositories")
    .refine((value) => {
    const ids = new Set(value.dependencies?.map((dependency) => dependency.repoId));
    return (ids.size === (value.dependencies?.length ?? 0) &&
        value.services.every((service) => !service.repoId || ids.has(service.repoId)));
}, "Select each dependency repository once and associate services with selected repositories")
    .refine((value) => !value.environmentFrom ||
    (value.dependencies ?? []).some((dependency) => dependency.repoId === value.environmentFrom.repoId), "The repository that defines the environment must be checked out beside this one")
    .refine((value) => !value.authMode || value.authMode === "public"
    ? value.authMode !== "public" || !value.authCommand
    : Boolean(value.authCheckSelector) &&
        (value.authMode === "human" || value.authMode === "cognito"
            ? !value.authCommand
            : Boolean(value.authCommand)), "Choose a sign-in mode, provide its command when unattended, and a visible authenticated-state check")
    .refine(
// Required for the mode, but deliberately kept when the mode changes: the
// saved pool is what still names the credentials to withhold from the
// application's own commands.
(value) => value.authMode !== "cognito" || Boolean(value.cognitoAuth), "Configure the Cognito user pool for Cognito sign-in")
    .refine(
// Only while the mode is active. A pool kept through a mode change must not
// constrain the mode that replaced it: the settings pane shows these fields
// in Cognito mode only, so a rule that fired otherwise could make an
// environment unsaveable with no way to clear the pool.
(value) => value.authMode !== "cognito" ||
    !value.cognitoAuth ||
    (installableOrigin(value, value.cognitoAuth.storageOrigin, SIGN_IN_PROVIDERS) &&
        // A provider denylist cannot know a customer's own sign-in domain, but
        // this environment has already named its sign-in providers in
        // `authOrigins`. `authStateOrigins` is not that list: it names the
        // application origins whose state a run captures, which is the same
        // category the session origin belongs to.
        !(value.authOrigins ?? []).includes(value.cognitoAuth.storageOrigin)), "Install the Cognito session only into an approved application origin, excluding sign-in providers")
    .refine(
// The sandbox's egress allowlist and the browser's own route filter are
// both built from allowedOrigins, so the pool's endpoint has to be an
// approved origin or the application's own Cognito calls are aborted
// client-side mid-run.
(value) => {
    if (value.authMode !== "cognito" || !value.cognitoAuth)
        return true;
    const endpoint = cognitoIdpOrigin(value.cognitoAuth.region);
    // An unusable region is the leaf schema's finding to report, not this
    // rule's to mask.
    return !endpoint || value.allowedOrigins.includes(endpoint);
}, "Approve the Cognito user pool endpoint as an application origin")
    .refine((value) => (value.authStateOrigins ?? []).every((origin) => installableOrigin(value, origin)), "Authentication state can include only approved application origins, excluding Google")
    .refine((value) => {
    // Postgres, Redis and MySQL listen beside the app services, so no two of
    // any of them may share a port.
    const managed = [value.postgres, value.redis, value.mysql].flatMap((service) => service ? [service.port] : []);
    const ports = new Set(value.services.map((service) => service.port));
    return new Set(managed).size === managed.length && managed.every((port) => !ports.has(port));
}, "The database and cache ports must differ from each other and from application service ports")
    .refine((value) => !value.authCommand || Boolean(value.authCheckSelector), "Provide a visible authenticated-state selector for the test identity")
    .refine((value) => new Set(value.services.map((service) => service.name)).size === value.services.length &&
    new Set(value.services.map((service) => service.port)).size === value.services.length, "Service names and ports must be unique");
/** A blank environment: what the schema fills in when nothing is set. */
export function qaEnvironmentDefaults() {
    return qaEnvironmentSchema.parse({});
}
//# sourceMappingURL=qaEnvironmentSchema.js.map